import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './PolicyViolationsDetailPage.css';

interface PolicyViolation {
  id: string;
  timestamp: string;
  violatedPolicy: string;
  violationType: 'Process' | 'File' | 'Network' | 'Capability' | 'System';
  namespace: string;
  pod: string;
  source: string; // 누가 트리거했는가 (프로세스/사용자)
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  count: number;
  lastSeen: string;
  status: 'Active' | 'Resolved';
  details: string;
}

const PolicyViolationsDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedViolation, setSelectedViolation] = useState<PolicyViolation | null>(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const violationData: PolicyViolation[] = useMemo(() => ([
    { 
      id: '1', 
      timestamp: '2024-01-15 14:35:22', 
      violatedPolicy: 'Deny Root Process Execution', 
      violationType: 'Process', 
      namespace: 'production', 
      pod: 'api-gateway-1', 
      source: '/bin/bash (uid: 0)',
      severity: 'Critical', 
      count: 5, 
      lastSeen: '2분 전', 
      status: 'Active',
      details: 'Unauthorized root-level process execution attempt detected'
    },
    { 
      id: '2', 
      timestamp: '2024-01-15 14:32:55', 
      violatedPolicy: 'Block External DNS', 
      violationType: 'Network', 
      namespace: 'default', 
      pod: 'web-app-1', 
      source: 'curl (uid: 1000)',
      severity: 'High', 
      count: 12, 
      lastSeen: '5분 전', 
      status: 'Active',
      details: 'Pod attempted DNS query to external resolver (8.8.8.8)'
    },
    { 
      id: '3', 
      timestamp: '2024-01-15 14:25:12', 
      violatedPolicy: 'Restrict System Call Access', 
      violationType: 'System', 
      namespace: 'kube-system', 
      pod: 'etcd-0', 
      source: 'etcd (uid: 65534)',
      severity: 'Critical', 
      count: 3, 
      lastSeen: '12분 전', 
      status: 'Active',
      details: 'Unauthorized system call (ptrace) detected from privileged process'
    },
    { 
      id: '4', 
      timestamp: '2024-01-15 14:10:45', 
      violatedPolicy: 'Whitelist File Access', 
      violationType: 'File', 
      namespace: 'production', 
      pod: 'worker-1', 
      source: 'java (uid: 999)',
      severity: 'High', 
      count: 8, 
      lastSeen: '27분 전', 
      status: 'Resolved',
      details: 'Attempt to access /etc/shadow outside of approved file paths'
    },
    { 
      id: '5', 
      timestamp: '2024-01-15 13:50:10', 
      violatedPolicy: 'Limit Linux Capabilities', 
      violationType: 'Capability', 
      namespace: 'default', 
      pod: 'db-1', 
      source: 'mysqld (uid: 999)',
      severity: 'Medium', 
      count: 15, 
      lastSeen: '47분 전', 
      status: 'Resolved',
      details: 'Process attempted to use CAP_SYS_ADMIN capability'
    },
    { 
      id: '6', 
      timestamp: '2024-01-15 13:45:38', 
      violatedPolicy: 'Block Network Egress', 
      violationType: 'Network', 
      namespace: 'default', 
      pod: 'cache-1', 
      source: 'redis-cli (uid: 1001)',
      severity: 'High', 
      count: 4, 
      lastSeen: '52분 전', 
      status: 'Resolved',
      details: 'Outbound connection to unauthorized IP address blocked'
    },
    { 
      id: '7', 
      timestamp: '2024-01-15 13:30:05', 
      violatedPolicy: 'Prevent Privileged Container', 
      violationType: 'Capability', 
      namespace: 'production', 
      pod: 'api-1', 
      source: 'init (uid: 0)',
      severity: 'Critical', 
      count: 2, 
      lastSeen: '1시간 7분 전', 
      status: 'Resolved',
      details: 'Container attempted to gain elevated privileges'
    },
    { 
      id: '8', 
      timestamp: '2024-01-15 13:15:30', 
      violatedPolicy: 'Monitor System Calls', 
      violationType: 'System', 
      namespace: 'staging', 
      pod: 'test-1', 
      source: 'python (uid: 1000)',
      severity: 'Medium', 
      count: 6, 
      lastSeen: '1시간 22분 전', 
      status: 'Resolved',
      details: 'Suspicious system call pattern detected: fork + execve sequence'
    },
  ]), []);

  const filteredViolations = useMemo(() => {
    return violationData.filter(violation => {
      const severityMatch = filterSeverity === 'all' || violation.severity === filterSeverity;
      const typeMatch = filterType === 'all' || violation.violationType === filterType;
      return severityMatch && typeMatch;
    });
  }, [filterSeverity, filterType, violationData]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return '#ff4444';
      case 'High': return '#ff8c00';
      case 'Medium': return '#ffd700';
      case 'Low': return '#22c55e';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'Active' ? '#ff4444' : '#22c55e';
  };

  return (
    <div className="policy-violations-container">
      <div className="detail-header">
        <h1>{t('policyViolations.detailTitle')}</h1>
        <p>{t('policyViolations.detailDescription')}</p>
      </div>

      {/* 필터 섹션 */}
      <section className="filter-section">
        <div className="filter-group">
          <label>{t('policyViolations.filters.violationType')}</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">{t('policyViolations.filters.all')}</option>
            <option value="Process">Process</option>
            <option value="File">File</option>
            <option value="Network">Network</option>
            <option value="Capability">Capability</option>
            <option value="System">System</option>
          </select>
        </div>
        <div className="filter-group">
          <label>{t('policyViolations.filters.severity')}</label>
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
            <option value="all">{t('policyViolations.filters.all')}</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        <div className="filter-stat">
          <span>{t('policyViolations.filters.searchResults')}: <strong>{filteredViolations.length}</strong></span>
        </div>
      </section>

      {/* 테이블 및 상세 정보 */}
      <div className="content-layout">
        {/* 데이터 테이블 */}
        <section className="table-section">
          <div className="table-header">
            <div className="col-policy">{t('policyViolations.columns.violatedPolicy')}</div>
            <div className="col-type">{t('policyViolations.columns.violationType')}</div>
            <div className="col-source">{t('policyViolations.columns.source')}</div>
            <div className="col-pod">Pod</div>
            <div className="col-severity">{t('policyViolations.columns.severity')}</div>
            <div className="col-status">{t('policyViolations.columns.status')}</div>
          </div>
          <div className="table-body">
            {filteredViolations.map((violation) => (
              <div
                key={violation.id}
                className={`table-row ${selectedViolation?.id === violation.id ? 'active' : ''}`}
                onClick={() => setSelectedViolation(violation)}
              >
                <div className="col-policy">{violation.violatedPolicy}</div>
                <div className="col-type">
                  <span className="type-badge" style={{backgroundColor: '#3b82f650'}}>
                    {violation.violationType}
                  </span>
                </div>
                <div className="col-source">{violation.source}</div>
                <div className="col-pod">{violation.pod}</div>
                <div className="col-severity">
                  <span className="severity-badge" style={{backgroundColor: `${getSeverityColor(violation.severity)}20`, color: getSeverityColor(violation.severity)}}>
                    {violation.severity}
                  </span>
                </div>
                <div className="col-status">
                  <span className="status-badge" style={{backgroundColor: `${getStatusColor(violation.status)}20`, color: getStatusColor(violation.status)}}>
                    {violation.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 상세 정보 */}
        <section className="detail-section">
          {selectedViolation ? (
            <div className="detail-card">
              <div className="detail-title">정책 위반 상세</div>
              <div className="detail-content">
                <div className="detail-row">
                  <div className="detail-label">위반 ID</div>
                  <div className="detail-value">{selectedViolation.id}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">정책명</div>
                  <div className="detail-value">{selectedViolation.violatedPolicy}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">위반 타입</div>
                  <div className="detail-value">{selectedViolation.violationType}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">원천 (Source)</div>
                  <div className="detail-value">{selectedViolation.source}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">네임스페이스</div>
                  <div className="detail-value">{selectedViolation.namespace}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Pod</div>
                  <div className="detail-value">{selectedViolation.pod}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">심각도</div>
                  <div className="detail-value">
                    <span style={{color: getSeverityColor(selectedViolation.severity)}}>
                      {selectedViolation.severity}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">상태</div>
                  <div className="detail-value">
                    <span style={{color: getStatusColor(selectedViolation.status)}}>
                      {selectedViolation.status}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">발생 시간</div>
                  <div className="detail-value">{selectedViolation.timestamp}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">마지막 감지</div>
                  <div className="detail-value">{selectedViolation.lastSeen}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">발생 횟수</div>
                  <div className="detail-value">{selectedViolation.count}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">상세 정보</div>
                  <div className="detail-value detail-description">{selectedViolation.details}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-state">
              <div className="empty-message">
                {t('common.selectFromList')}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PolicyViolationsDetailPage;
